// ─────────────────────────────────────────────────────────────
// InwardReportModal.jsx — Report 10 filter window
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WindowModal, { DEFAULT_FROM_DATE, todayISO } from '../../components/WindowModal';
import PrintButton from '../../print/PrintButton';
import { inwardReportConfig } from './inwardReport.config';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  text: '#1a2332',
  textSoft: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
};

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const InwardReportModal = ({ onClose }) => {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
  const [toDate, setToDate] = useState(todayISO());
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // All vendors, not just active ones — a historic receipt may
  // belong to a vendor who has since been deactivated, and it
  // would be invisible in the filter otherwise.
  useEffect(() => {
    axios.get(`${API}/tape/vendors`, { headers })
      .then((res) => setVendors(res.data.data || []))
      .catch(() => setVendors([]));
  }, []); // eslint-disable-line

  const buildConfig = async () => {
    setError('');
    const params = new URLSearchParams();
    if (vendorId) params.append('vendor_id', vendorId);
    params.append('from_date', fromDate);
    params.append('to_date', toDate);

    const res = await axios.get(`${API}/tape/inward?${params.toString()}`, { headers });
    const inwards = res.data.data || [];

    if (inwards.length === 0) {
      setError('No receipts match these filters.');
      return null;
    }

    const vendor = vendors.find((v) => String(v.id) === String(vendorId));

    return inwardReportConfig(inwards, {
      vendor_name: vendor ? vendor.name : '',
      from_date: fromDate,
      to_date: toDate,
    });
  };

  return (
    <WindowModal
      id="inward-report"
      title="Inward Report"
      subtitle="Goods received, by vendor and date range."
      onClose={onClose}
      width="620px"
    >
      <div style={{ padding: '22px 24px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px', marginBottom: '20px',
        }}>
          <Field label="Vendor">
            <select
              style={inputStyle}
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
            >
              <option value="">All vendors</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </Field>

          <Field label="From date">
            <input
              type="date"
              style={inputStyle}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Field>

          <Field label="To date">
            <input
              type="date"
              style={inputStyle}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Field>
        </div>

        {error && (
          <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <PrintButton
            label="Download PDF"
            filename={`Inward-Report-${fromDate}-to-${toDate}`}
            getConfig={buildConfig}
          />
          <PrintButton label="Print" mode="print" getConfig={buildConfig} />
          <span style={{ fontSize: '12px', color: C.textMuted, marginLeft: 'auto' }}>
            Vendor blank = all vendors
          </span>
        </div>
      </div>
    </WindowModal>
  );
};

export default InwardReportModal;